// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { createContext } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import mapValues from 'lodash/mapValues';
import { Density, Mode } from '@cloudscape-design/global-styles';
import { THEME } from '~components/internal/environment';
const appContextDefaults = {
    mode: Mode.Light,
    pageId: undefined,
    urlParams: {
        density: Density.Comfortable,
        direction: 'ltr',
        visualRefresh: THEME === 'default',
        motionDisabled: false,
        appLayoutWidget: false,
    },
    setMode: () => { },
    setUrlParams: () => { },
};
const AppContext = createContext(appContextDefaults);
export default AppContext;
export function parseQuery(query) {
    const queryParams = Object.assign({}, appContextDefaults.urlParams);
    const urlParams = new URLSearchParams(query);
    urlParams.forEach((value, key) => (queryParams[key] = value));
    return mapValues(queryParams, value => {
        if (value === 'true' || value === 'false') {
            return value === 'true';
        }
        return value;
    });
}
function formatQuery(params) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === appContextDefaults.urlParams[key]) {
            continue;
        }
        query.set(key, value);
    }
    return query ? `?${query.toString()}` : '';
}
export function AppContextProvider({ children }) {
    const history = useHistory();
    const location = useLocation();
    const match = useRouteMatch('/:mode(light|dark)/:pageId*');
    const { mode, pageId } = match ? match.params : { mode: undefined, pageId: undefined };
    const urlParams = parseQuery(location.search);
    function setUrlParams(newParams) {
        const pathname = [mode, pageId].filter(segment => !!segment).join('/') + '/';
        history.replace(`/${pathname}${formatQuery(Object.assign(Object.assign({}, urlParams), newParams))}`);
    }
    function updateMode(newMode) {
        const pathname = [newMode, pageId].filter(segment => !!segment).join('/') + '/';
        history.replace('/' + pathname + location.search + location.hash);
    }
    return (React.createElement(AppContext.Provider, { value: { mode: mode, pageId, urlParams, setUrlParams: setUrlParams, setMode: updateMode } }, children));
}
